/**
 * Derive a full Tailwind-style 50-900 color scale from a single base color.
 *
 * Used by applyBrandTheme to turn the deployment-level APP_PRIMARY branding
 * value into the --primary-* CSS variables that back the app's `primary-*`
 * palette (see the @theme block in src/styles/globals.css). The base color
 * is used verbatim as the 500 shade; the other steps keep its hue/saturation
 * and follow the lightness ramp of the stock Tailwind scales. Mid-lightness
 * bases (~L 55-65%) produce the most balanced ramps.
 */

export interface Rgb {
  r: number
  g: number
  b: number
}

/** Accepts "#RRGGBB" hex or a "r g b" channel triplet (the --brand-* convention). */
export function parseColor(value: string): Rgb | null {
  const v = value.trim()
  const hex = /^#?([0-9a-f]{6})$/i.exec(v)
  if (hex) {
    const n = parseInt(hex[1], 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  const triplet = /^(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})$/.exec(v)
  if (triplet) {
    const [r, g, b] = triplet.slice(1).map(Number)
    if ([r, g, b].every((c) => c <= 255)) return { r, g, b }
  }
  return null
}

// Lightness (HSL, 0-1) per step, matching the stock Tailwind blue ramp.
// 500 is intentionally absent — the configured color is used verbatim.
const LIGHTNESS_RAMP: Array<[step: number, l: number]> = [
  [50, 0.97],
  [100, 0.93],
  [200, 0.87],
  [300, 0.78],
  [400, 0.68],
  [600, 0.53],
  [700, 0.48],
  [800, 0.4],
  [900, 0.33],
]

function rgbToHsl({ r, g, b }: Rgb): [h: number, s: number, l: number] {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
  else if (max === gn) h = ((bn - rn) / d + 2) / 6
  else h = ((rn - gn) / d + 4) / 6
  return [h, s, l]
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  }
}

/** Returns "r g b" channel triplets keyed by scale step (50-900). */
export function derivePrimaryScale(base: Rgb): Record<number, string> {
  const [h, s] = rgbToHsl(base)
  const scale: Record<number, string> = {
    500: `${base.r} ${base.g} ${base.b}`,
  }
  for (const [step, l] of LIGHTNESS_RAMP) {
    const { r, g, b } = hslToRgb(h, s, l)
    scale[step] = `${r} ${g} ${b}`
  }
  return scale
}
